import { useRouter } from "next/navigation";
import { TYPO } from "styles/Typo";
import { usePortfolioLastView } from "utils/clientAPIs/portfolio";
import { useEffect, useRef, useState } from "react";
import { useTrackedNavigation } from "lib/analytics/react/hooks";
function MainPortfolioHighlight({
  portId,
  isRecommandActive,
}: {
  portId: number | null;
  isRecommandActive: boolean;
}) {
  const router = useRouter();
  const [isAtTop, setIsAtTop] = useState(true);
  const isAtTopRef = useRef(true);
  const tickingRef = useRef(false);
  const lockUntilRef = useRef(0);
  const { navigateWithTracking } = useTrackedNavigation();
  useEffect(() => {
    const ENTER_TOP_THRESHOLD = 4; // px
    const LEAVE_TOP_THRESHOLD = 40; // px (wider hysteresis to avoid bounce)
    const STATE_CHANGE_LOCK_MS = 400; // lock updates during height animation

    const update = () => {
      const rawY = window.scrollY || 0;
      const y = Math.max(0, Math.round(rawY));
      const now = performance.now();

      if (now < lockUntilRef.current) {
        tickingRef.current = false;
        return;
      }
      let nextIsAtTop: boolean;
      if (isAtTopRef.current) {
        nextIsAtTop = y <= LEAVE_TOP_THRESHOLD;
      } else {
        nextIsAtTop = y <= ENTER_TOP_THRESHOLD;
      }
      if (nextIsAtTop !== isAtTopRef.current) {
        isAtTopRef.current = nextIsAtTop;
        setIsAtTop(nextIsAtTop);
        lockUntilRef.current = now + STATE_CHANGE_LOCK_MS;
      }
      tickingRef.current = false;
    };

    const onScroll = () => {
      if (!tickingRef.current) {
        tickingRef.current = true;
        requestAnimationFrame(update);
      }
    };

    // init
    const initY = Math.max(0, Math.round(window.scrollY || 0));
    isAtTopRef.current = initY <= ENTER_TOP_THRESHOLD;
    setIsAtTop(isAtTopRef.current);

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const meta = [
    {
      title: "처방전",
      icon: RecommendIcon,
      type: "recommend",
      dataGtmId: "main-pf-highlight-recommend-btn",
      nav_key: "portfolio_navbtn_highlight_dig",
    },
    {
      title: "투자매력도",
      icon: ScoreIcon,
      type: "score",
      dataGtmId: "main-pf-highlight-score-btn",
      nav_key: "portfolio_navbtn_highlight_attractiveness",
    },
    {
      title: "분산",
      icon: SimIcon,
      type: "sim",
      dataGtmId: "main-pf-highlight-sim-btn",
      nav_key: "portfolio_navbtn_highlight_corr",
    },
    {
      title: "변동성",
      icon: VolatilityIcon,
      type: "risk",
      dataGtmId: "main-pf-highlight-weight-btn",
      nav_key: "portfolio_navbtn_highlight_volatility",
    },
  ];
  return (
    <div className="flex gap-[6px] items-center px-4 py-4">
      {meta.map((item, index) => (
        <div
          key={index}
          className="flex flex-col items-center justify-center cursor-pointer grow flex-1 shrink-0 w-full"
          onClick={() => {
            if (item.type === "recommend") {
              navigateWithTracking({
                href: `/portfolio/${portId}/recommand?from=main`,
                itemspec: {
                  kind: "section_nav",
                  section_id: "portfolio_highlight",
                  nav_key: item.nav_key,
                },
              });
              return;
            }
            navigateWithTracking({
              href: `/portfolio/highlight/${portId}?type=${item.type}`,
              itemspec: {
                kind: "section_nav",
                section_id: "portfolio_highlight",
                nav_key: item.nav_key,
              },
            });
            router.push(`/portfolio/highlight/${portId}?type=${item.type}`);
          }}
          data-gtm-id={item.dataGtmId}
        >
          <div
            className={`bg-white  flex items-center justify-center w-full flex-col transition-all duration-300 ease-out ${
              isAtTop
                ? "py-3 gap-1 rounded-[14px]"
                : "py-[6px] gap-0 rounded-[10px]"
            }`}
            style={{
              borderColor:
                isRecommandActive && item.type === "recommend"
                  ? "var(--blue40)"
                  : "var(--hairlineBorder)",
            }}
          >
            <div
              className={`transition-all duration-300  transform-gpu origin-center overflow-hidden  ${
                isAtTop
                  ? "h-[26px] scale-100 opacity-100"
                  : "h-0 scale-0 opacity-0"
              }`}
            >
              <item.icon />
            </div>
            <p
              className={`${
                isAtTop ? TYPO.TYPO8_MD : TYPO.TYPO7_MD
              } text-gray70 flex items-center transition-all duration-300 `}
            >
              {item.title}
              {item.title === "처방전" && (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="17"
                  height="16"
                  viewBox="0 0 17 16"
                  fill="none"
                >
                  <mask
                    id="mask0_23163_24545"
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="17"
                    height="16"
                  >
                    <rect x="0.625" width="16" height="16" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_23163_24545)">
                    <path
                      d="M11.2922 5.60016L5.35885 11.5335C5.23663 11.6557 5.08108 11.7168 4.89219 11.7168C4.7033 11.7168 4.54774 11.6557 4.42552 11.5335C4.3033 11.4113 4.24219 11.2557 4.24219 11.0668C4.24219 10.8779 4.3033 10.7224 4.42552 10.6002L10.3589 4.66683H5.29219C5.1033 4.66683 4.94497 4.60294 4.81719 4.47516C4.68941 4.34739 4.62552 4.18905 4.62552 4.00016C4.62552 3.81127 4.68941 3.65294 4.81719 3.52516C4.94497 3.39738 5.1033 3.3335 5.29219 3.3335H11.9589C12.1477 3.3335 12.3061 3.39738 12.4339 3.52516C12.5616 3.65294 12.6255 3.81127 12.6255 4.00016V10.6668C12.6255 10.8557 12.5616 11.0141 12.4339 11.1418C12.3061 11.2696 12.1477 11.3335 11.9589 11.3335C11.77 11.3335 11.6116 11.2696 11.4839 11.1418C11.3561 11.0141 11.2922 10.8557 11.2922 10.6668V5.60016Z"
                      fill="#4E5968"
                    />
                  </g>
                </svg>
              )}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

const RecommendIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="26"
    viewBox="0 0 27 26"
    fill="none"
  >
    <g clip-path="url(#clip0_23163_24556)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.32028 8.69775L4.37703 13.641C1.99932 16.0187 1.99932 19.8732 4.37703 22.2503C6.75473 24.628 10.6099 24.628 12.9863 22.2503L17.9295 17.307L9.32028 8.69775Z"
        fill="#FFDA16"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M22.8728 12.3642C25.2505 9.9865 25.2505 6.132 22.8728 3.75495C20.4951 1.37725 16.6406 1.37725 14.2636 3.75495L9.32031 8.6982L17.9296 17.3075L22.8728 12.3642Z"
        fill="#EF4452"
      />
    </g>
    <defs>
      <clipPath id="clip0_23163_24556">
        <rect
          width="26"
          height="26"
          fill="white"
          transform="translate(0.625)"
        />
      </clipPath>
    </defs>
  </svg>
);

const ScoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="26"
    viewBox="0 0 27 26"
    fill="none"
  >
    <g clipPath="url(#clip0_23163_24596)">
      <path
        d="M18.8828 10.3322H25.9139L21.7734 2.99756H21.7727L18.8828 10.3322Z"
        fill="#7D9DFF"
      />
      <path
        d="M5.99987 2.99756L1.85938 10.3322H8.89043L5.99987 2.99756Z"
        fill="#7D9DFF"
      />
      <path
        d="M18.8785 10.3345H8.89062L13.8846 23.0095L18.8785 10.3345Z"
        fill="#7D9DFF"
      />
      <path
        d="M8.89062 10.3322H18.8785L13.8846 2.99756L8.89062 10.3322Z"
        fill="#5A81FF"
      />
      <path
        d="M1.85938 10.3345L13.8844 23.0095L8.89043 10.3345H1.85938Z"
        fill="#A8BFFF"
      />
      <path
        d="M18.869 10.3345L13.875 23.0095L25.9 10.3345H18.869Z"
        fill="#A8BFFF"
      />
      <path
        d="M13.8767 2.99756H5.99219L8.88274 10.3322L13.8767 2.99756Z"
        fill="#A8BFFF"
      />
      <path
        d="M18.869 10.3322L21.7589 2.99756H13.875L18.869 10.3322Z"
        fill="#A8BFFF"
      />
    </g>
    <defs>
      <clipPath id="clip0_23163_24596">
        <rect
          width="26"
          height="26"
          fill="white"
          transform="translate(0.875)"
        />
      </clipPath>
    </defs>
  </svg>
);

const SimIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="26"
    viewBox="0 0 27 26"
    fill="none"
  >
    <g clipPath="url(#clip0_23174_23821)">
      <path
        d="M20.7217 14.625H5.53125C4.6336 14.625 3.90625 13.8977 3.90625 13C3.90625 12.1023 4.6336 11.375 5.53125 11.375H20.7217C21.6194 11.375 22.3468 12.1023 22.3468 13C22.3468 13.8977 21.6194 14.625 20.7217 14.625Z"
        fill="#313D4C"
      />
      <path
        d="M13.1184 9.29498C14.4826 9.29498 15.5884 8.18912 15.5884 6.82498C15.5884 5.46084 14.4826 4.35498 13.1184 4.35498C11.7543 4.35498 10.6484 5.46084 10.6484 6.82498C10.6484 8.18912 11.7543 9.29498 13.1184 9.29498Z"
        fill="#313D4C"
      />
      <path
        d="M13.1184 21.6451C14.4826 21.6451 15.5884 20.5392 15.5884 19.1751C15.5884 17.8109 14.4826 16.7051 13.1184 16.7051C11.7543 16.7051 10.6484 17.8109 10.6484 19.1751C10.6484 20.5392 11.7543 21.6451 13.1184 21.6451Z"
        fill="#313D4C"
      />
    </g>
    <defs>
      <clipPath id="clip0_23174_23821">
        <rect
          width="26"
          height="26"
          fill="white"
          transform="translate(0.125)"
        />
      </clipPath>
    </defs>
  </svg>
);

const VolatilityIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="27"
    height="26"
    viewBox="0 0 27 26"
    fill="none"
  >
    <path
      d="M16.7791 8.57096C16.9208 8.54236 17.067 8.52676 17.2172 8.52676C17.7463 8.52676 18.2253 8.72241 18.6036 9.03506C18.9819 8.72241 19.461 8.52676 19.9901 8.52676C20.2436 8.52676 20.4834 8.57876 20.7103 8.65806C20.1935 6.30506 18.4671 4.40966 16.2149 3.65111C15.5285 3.41971 14.7953 3.28906 14.0309 3.28906C12.0048 3.28906 10.1901 4.17501 8.9375 5.57381C9.6811 5.23646 10.504 5.04341 11.3737 5.04341C13.7891 5.04341 15.8632 6.49486 16.7791 8.57096Z"
      fill="#64A7FF"
    />
    <path
      d="M5.68197 10.047C5.70797 9.98849 5.73658 9.93194 5.76518 9.87409C5.84123 9.72005 5.92508 9.5699 6.01868 9.4269C6.04338 9.38919 6.06808 9.35279 6.09343 9.31574C6.19548 9.1695 6.30858 9.03104 6.42753 8.89844C6.46133 8.86139 6.49382 8.82369 6.52827 8.7873C6.66217 8.6482 6.80582 8.51819 6.95727 8.39859C6.99172 8.37129 7.02748 8.34595 7.06258 8.31995C7.22573 8.1984 7.39537 8.08399 7.57477 7.98649C7.57802 7.98454 7.58193 7.98325 7.58518 7.9813C7.76718 7.88315 7.95893 7.8019 8.15523 7.72975C8.19228 7.7161 8.22868 7.7018 8.26573 7.68945C8.45942 7.62444 8.65897 7.5731 8.86437 7.53605C8.90337 7.52889 8.94368 7.52369 8.98333 7.51785C9.19848 7.48469 9.41687 7.46259 9.64047 7.46259H9.64177C11.0029 7.46259 12.2158 8.09245 13.0088 9.0746C13.3936 8.7379 13.8915 8.5273 14.4427 8.5273C14.9718 8.5273 15.4508 8.72294 15.8291 9.03559C16.1002 8.81135 16.42 8.6443 16.7781 8.5715C15.8623 6.49475 13.7888 5.04395 11.3734 5.04395C10.5037 5.04395 9.68078 5.23635 8.93718 5.57435C6.99693 6.4551 5.62152 8.35114 5.48438 10.5858C5.48698 10.5767 5.49152 10.5683 5.49412 10.5585C5.54612 10.383 5.60917 10.2127 5.68197 10.0476V10.047Z"
      fill="#93C9FF"
    />
    <path
      d="M20.7063 8.65797C20.9527 8.74442 21.1756 8.87247 21.3726 9.03497C21.7509 8.72232 22.2299 8.52667 22.759 8.52667C23.0112 8.52667 23.2498 8.57802 23.4753 8.65667C22.512 5.74077 19.7697 3.63477 16.5314 3.63477C16.4235 3.63477 16.3182 3.64647 16.2109 3.65102C18.4632 4.40957 20.1896 6.30497 20.7063 8.65797Z"
      fill="#2F81F9"
    />
    <path
      d="M21.7812 22.3793C21.0272 22.6458 20.6437 23.0943 19.3437 23.0943C17.5042 23.0943 17.5042 22.1973 15.6647 22.1973C13.8252 22.1973 13.8252 23.0943 11.9857 23.0943C10.1462 23.0943 10.1462 22.1973 8.31322 22.1973C6.48022 22.1973 6.47371 23.0943 4.64071 23.0943C3.26271 23.0943 2.91821 22.5873 2.05371 22.3338C1.61171 22.2038 1.47521 21.6578 1.75471 21.2938C3.94521 18.4598 4.53021 14.3973 5.37521 11.0888C5.40121 10.9328 5.43371 10.7768 5.47921 10.6208C6.26571 9.60035 7.50721 8.94385 8.89821 8.94385C10.9132 8.94385 11.9402 10.6728 12.5317 11.7908C12.6227 11.9598 12.7202 12.0833 12.8177 12.1743C13.0907 12.4733 13.4417 12.6943 13.8447 12.8113C14.0332 12.8633 14.2347 12.9023 14.4427 12.9023C14.9692 12.9023 15.4502 12.7073 15.8272 12.3953C16.2042 12.7073 16.6852 12.9023 17.2182 12.9023C17.7447 12.9023 18.2257 12.7073 18.6027 12.3953C18.7847 12.5448 18.9862 12.6683 19.2137 12.7593C18.7197 14.9563 19.8117 17.7968 21.0727 20.0198C21.3587 20.5268 21.7292 20.9753 22.0607 21.3263C22.3857 21.6708 22.2297 22.2298 21.7812 22.3793Z"
      fill="#2F81F9"
    />
    <path
      d="M8.89489 8.94521C10.9099 8.94521 11.9375 10.6742 12.5303 11.7896C12.6207 11.9593 12.7169 12.0808 12.8144 12.1718C12.4666 11.7844 12.2489 11.2774 12.2489 10.7158C12.2489 10.0587 12.5446 9.47561 13.0035 9.07391C12.2105 8.09111 10.9976 7.46191 9.63654 7.46191H9.63524C9.41164 7.46191 9.19259 7.48401 8.97809 7.51716C8.93844 7.52366 8.89879 7.52821 8.85914 7.53536C8.65439 7.57241 8.45419 7.62376 8.26049 7.68876C8.22279 7.70111 8.18704 7.71541 8.14999 7.72906C7.95369 7.80121 7.76194 7.88246 7.57994 7.98061C7.57669 7.98256 7.57279 7.98386 7.56954 7.98581C7.38949 8.08331 7.22049 8.19771 7.05734 8.31926C7.02224 8.34526 6.98649 8.37061 6.95204 8.39791C6.80059 8.51816 6.65759 8.64816 6.52304 8.78661C6.48794 8.82236 6.45544 8.86071 6.42229 8.89776C6.30269 9.03036 6.19024 9.16881 6.08819 9.31506C6.06284 9.35146 6.03814 9.38851 6.01344 9.42621C5.91984 9.56921 5.83664 9.71936 5.75994 9.87341C5.73134 9.93061 5.70274 9.98781 5.67674 10.0463C5.60394 10.2114 5.54089 10.3817 5.48889 10.5572C5.48629 10.5663 5.48174 10.5748 5.47914 10.5845C5.47394 10.6021 5.46549 10.6177 5.46094 10.6352C5.46484 10.63 5.46939 10.6261 5.47329 10.6209C6.26499 9.60301 7.50584 8.94521 8.89489 8.94521Z"
      fill="#104BE0"
    />
    <path
      d="M24.951 10.7188C24.951 11.9278 23.9695 12.9028 22.7605 12.9028C22.2275 12.9028 21.753 12.7078 21.376 12.3958C20.9925 12.7078 20.518 12.9028 19.985 12.9028C19.712 12.9028 19.452 12.8508 19.2115 12.7598C18.984 12.6688 18.7825 12.5453 18.6005 12.3958C18.2235 12.7078 17.7425 12.9028 17.216 12.9028C16.683 12.9028 16.202 12.7078 15.825 12.3958C15.448 12.7078 14.967 12.9028 14.4405 12.9028C14.2325 12.9028 14.031 12.8638 13.8425 12.8118C13.4395 12.6948 13.0885 12.4738 12.8155 12.1748C12.471 11.7848 12.25 11.2778 12.25 10.7188C12.25 10.0558 12.549 9.47732 13.004 9.07432C13.3875 8.73632 13.888 8.52832 14.4405 8.52832C14.967 8.52832 15.448 8.72332 15.825 9.03532C16.098 8.81432 16.4165 8.64532 16.774 8.57382C16.917 8.54132 17.06 8.52832 17.216 8.52832C17.7425 8.52832 18.2235 8.72332 18.6005 9.03532C18.9775 8.72332 19.4585 8.52832 19.985 8.52832C20.2385 8.52832 20.479 8.58032 20.7065 8.65832C20.9535 8.74282 21.1745 8.87282 21.376 9.03532C21.753 8.72332 22.2275 8.52832 22.7605 8.52832C23.014 8.52832 23.248 8.58032 23.4755 8.65832C24.3335 8.95732 24.951 9.75682 24.951 10.7188Z"
      fill="#E4EBF7"
    />
  </svg>
);

export default MainPortfolioHighlight;
